import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

const AboutPage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [storyRef, storyInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [valuesRef, valuesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const timeline = [
    { year: '2018', event: 'Started consulting journey' },
    { year: '2020', event: 'Specialized in e-commerce' },
    { year: '2022', event: 'Added wellness coaching' },
    { year: '2024', event: 'Served 200+ clients' },
  ];

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Mission',
      description: 'To empower entrepreneurs and individuals with the strategies, tools, and support they need to build thriving businesses and live balanced, fulfilling lives.',
    },
    {
      icon: 'ri-eye-line',
      title: 'Vision',
      description: 'Creating a community where business success and personal wellbeing go hand in hand, inspiring sustainable growth and holistic prosperity.',
    },
    {
      icon: 'ri-heart-line',
      title: 'Values',
      description: 'Integrity, excellence, empathy, and continuous growth. We believe in authentic relationships, data-driven strategies, and personalized approaches.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Introduction */}
        <section ref={heroRef} className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <nav className="text-sm text-gray-500 mb-6">
                <span>Home</span> <span className="mx-2">&gt;</span> <span className="text-primary-gold">About</span>
              </nav>

              <h1 className="font-serif font-bold text-5xl lg:text-6xl text-primary-charcoal mb-4">
                Meet Titilayo Ajayi
              </h1>
              <p className="text-xl text-primary-gold font-medium mb-8">Founder of ThriveEdge</p>

              <div className="flex justify-center mb-8">
                <div className="relative w-48 h-48 rounded-full overflow-hidden border-4 border-primary-gold shadow-xl">
                  <img
                    src="https://public.readdy.ai/ai/img_res/a025ebebbb9afe7037be1192993775c7.jpg"
                    alt="Titilayo Ajayi"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
                I'm <strong>Titilayo Ajayi</strong>, a passionate business consultant, e-commerce strategist, and wellness coach dedicated to helping entrepreneurs build successful businesses while maintaining their health and wellbeing. With over 5 years of experience, I've helped more than 200 clients transform their businesses and lives.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Story Section */}
        <section ref={storyRef} className="py-24 bg-primary-beige">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              {/* Left: Story */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={storyInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <h2 className="font-serif font-bold text-4xl text-primary-charcoal mb-6">My Journey</h2>
                
                <p className="text-gray-700 leading-relaxed">
                  My journey into business consulting and wellness coaching began with a personal realization: success means nothing without health and balance. After years of working in the corporate world, I witnessed countless entrepreneurs burning out while chasing their dreams.
                </p>

                <p className="text-gray-700 leading-relaxed">
                  I decided to bridge the gap between business growth and personal wellbeing. I pursued certifications in business strategy and wellness coaching, combining my passion for entrepreneurship with a commitment to holistic health.
                </p>

                <p className="text-gray-700 leading-relaxed">
                  Today, through <strong>ThriveEdge</strong>, I help business owners scale their operations, optimize their e-commerce platforms, and maintain the energy and mindset needed for sustainable success. My approach is simple: grow your business without sacrificing your wellbeing.
                </p>

                <p className="text-gray-700 leading-relaxed">
                  Every client I work with receives personalized strategies tailored to their unique goals, challenges, and lifestyle. Because I believe that true success is measured not just in profits, but in peace of mind, health, and fulfillment.
                </p>
              </motion.div>

              {/* Right: Timeline */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={storyInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white rounded-3xl p-8 shadow-xl"
              >
                <h3 className="font-serif font-bold text-2xl text-primary-charcoal mb-8">Milestones</h3>
                
                <div className="space-y-8">
                  {timeline.map((item, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-gold rounded-full text-white font-bold">
                        {item.year.slice(-2)}
                      </div>
                      <div className="flex-1 pt-2">
                        <div className="text-sm text-gray-500 mb-1">{item.year}</div>
                        <div className="text-gray-700 font-medium">{item.event}</div>
                      </div>
                      {index < timeline.length - 1 && (
                        <div className="absolute left-[30px] mt-12 w-0.5 h-8 bg-primary-sage opacity-30"></div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mission, Vision, Values */}
        <section ref={valuesRef} className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={valuesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-serif font-bold text-4xl text-primary-charcoal">What Drives Me</h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-primary-beige rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-primary-sage rounded-full mb-6">
                    <i className={`${value.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="font-serif font-bold text-2xl text-primary-charcoal mb-4">{value.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Personal Touch */}
        <section className="relative py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=peaceful%20wellness%20coaching%20session%20in%20bright%20airy%20space%2C%20African%20woman%20coach%20consulting%20with%20client%20in%20comfortable%20modern%20setting%2C%20natural%20plants%20and%20sunlight%2C%20warm%20inviting%20atmosphere%20showing%20authentic%20connection%20and%20professional%20guidance%20for%20health%20and%20business%20balance&width=1920&height=800&seq=about-personal-1&orientation=landscape"
              alt="Coaching Session"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center text-white">
            <h2 className="font-serif font-bold text-4xl lg:text-5xl mb-6">Beyond Business</h2>
            <p className="text-lg leading-relaxed mb-8">
              When I'm not consulting or coaching, you'll find me exploring new wellness practices, spending quality time with family, reading about personal development, and continuously learning new strategies to better serve my clients. I believe that to help others grow, I must commit to my own growth journey.
            </p>
            <p className="text-lg leading-relaxed">
              My philosophy is simple: <strong>success without fulfillment is failure</strong>. That's why I'm committed to helping you achieve both.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-primary-gold">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center text-white">
            <h2 className="font-serif font-bold text-4xl lg:text-5xl mb-6">Let's Work Together</h2>
            <p className="text-xl mb-10">Ready to transform your business and wellbeing? Let's have a conversation.</p>
            
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-10 py-5 bg-white text-primary-charcoal hover:bg-primary-beige font-bold text-lg rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap"
            >
              Book a Discovery Call
              <i className="ri-calendar-line ml-3 text-xl"></i>
            </a>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 mt-10">
              <a href="mailto:titilayoajayi923@gmail.com" className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer">
                <i className="ri-mail-line text-xl"></i>
                <span>Email</span>
              </a>
              <a href="tel:+2349060814354" className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer">
                <i className="ri-phone-line text-xl"></i>
                <span>Phone</span>
              </a>
              <a href="https://wa.me/2349047390448" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer">
                <i className="ri-whatsapp-line text-xl"></i>
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default AboutPage;
